import type { AssetRegistry } from "../assets/AssetRegistry";
import type { GameBootDefinition } from "../composition/GameDefinition";
import { loadLocalizationDictionaryFromBundle } from "../localization/LocalizationLoader";
import type { LocalizationService } from "../localization/LocalizationService";
import type { LocalizationDictionary } from "../localization/LocalizationTypes";
import { RuntimeLocalizationService } from "../localization/RuntimeLocalizationService";
import { shellEnDictionary } from "../localization/shell.en";
import { SHELL_BUNDLE_NAMES } from "./defaults/BundleNames";
import type { Log } from "@slot/presentation";

const ENGLISH = "en";

/** "en-US" → "en", "pt_BR" → "pt"; a bare code is its own base. */
function baseLanguage(locale: string): string {
  return locale.split(/[-_]/)[0]!.toLowerCase();
}

/**
 * The localization service: which language is active, and what a player sees when a string is
 * missing from it.
 *
 * - **Whose words.** A locale's dictionary is the shell's served `shell-ui:localization/shell.<locale>`,
 *   loaded here, with the title's (`GameLocaleProvider.loadRemoteDictionary`, its `game.<locale>`)
 *   merged over it. Until decision 40 the title loaded both, so a title that left the merge out
 *   loaded no served language at all (boundary review 2026-10-01 #1, #2).
 * - **Which language.** The requested locale (the config override, else the game link's `lang`, decision 81, else
 *   the settings'), then its base language ("en-US" → "en"), then English. The first that actually has a dictionary
 *   wins.
 * - **A missing string.** It shows exactly as it would for an English player: the served English
 *   dictionary, over the compiled one. So a language with no files, a language missing one of its
 *   files, and a single missing key all read as English rather than a raw `{info.rtp.body}`.
 *
 * Until 2026-09-23 a locale with no files loaded as an EMPTY dictionary, was made active, and was
 * logged as loaded. The compiled English beneath it lacks this game's rules text, so the info page
 * — the RTP, volatility and max-win disclosures — rendered as placeholders; staging, which asks for
 * "en-US" where only "en" exists, showed exactly that (review finding 16; user: fall back to
 * English). The served language files sit in the core bundles the boot has already downloaded, so
 * the English floor costs no extra request.
 */
export async function createBootLocalization(input: { game: GameBootDefinition; assets: AssetRegistry; localeCodeOverride: string; linkLocaleCode: string | null; settingsLocaleCode: string; log: Log }): Promise<LocalizationService> {
  const provider = input.game.locale;
  const loadRemoteDictionary = provider.loadRemoteDictionary;
  const shellBundle = input.assets.bundleJson(SHELL_BUNDLE_NAMES.shellUi);
  const gameBundle = input.assets.bundleJson(SHELL_BUNDLE_NAMES.gameSharedVisuals);
  // A served file that is missing, unreadable or not a dictionary is an empty dictionary: the English floor covers it.
  // Both loaders reject on a missing file (the bundle source and `loadLocalizationDictionaryFromBundle`), so the
  // `undefined` Pixi resolves for an alias no bundle lists never reaches `Object.keys` (staging's "en-US", 2026-10-04).
  const loadServed = async (label: string, run: () => Promise<LocalizationDictionary>): Promise<LocalizationDictionary> => {
    try {
      return await run();
    } catch (error) {
      input.log(`[Localization] no ${label}: ${error instanceof Error ? error.message : String(error)}`);
      return {};
    }
  };
  const loaded = new Map<string, LocalizationDictionary>();
  // A locale's dictionary: the shell's served file, the title's merged over it (game over shell).
  const load = async (locale: string): Promise<LocalizationDictionary> => {
    const cached = loaded.get(locale);
    if (cached) return cached;
    const shell = await loadServed(`shell.${locale}`, () => loadLocalizationDictionaryFromBundle(shellBundle, `localization/shell.${locale}`));
    const game = loadRemoteDictionary ? await loadServed(`game.${locale}`, () => loadRemoteDictionary({ locale, gameBundleRegistry: gameBundle })) : {};
    input.log(`[Localization] "${locale}": shell ${Object.keys(shell).length} keys, game ${Object.keys(game).length} keys`);
    const dictionary = { ...shell, ...game };
    loaded.set(locale, dictionary);
    return dictionary;
  };

  // The floor under every other language: what an English player sees, served text over compiled.
  const servedEnglish = await load(ENGLISH);
  const localization = new RuntimeLocalizationService({
    fallbackLocale: ENGLISH,
    fallbackDictionary: { ...shellEnDictionary, ...provider.baseDictionary, ...servedEnglish },
    remoteLoader: load,
  });

  const requested = input.localeCodeOverride.trim() || input.linkLocaleCode || input.settingsLocaleCode || ENGLISH;
  let active = ENGLISH;
  for (const locale of new Set([requested, baseLanguage(requested)])) {
    if (locale === ENGLISH) break;
    await localization.loadLocale(locale);
    if (Object.keys(loaded.get(locale) ?? {}).length > 0) {
      active = locale;
      break;
    }
    input.log(`[Localization] no dictionary for "${locale}"`);
  }
  // English as the ACTIVE dictionary too, not only the floor, so an English player's lookups read
  // the served file first exactly as they always have.
  await localization.loadLocale(active);
  localization.setLocale(active);
  input.log(active === requested ? `[Localization] loaded locale "${active}"` : `[Localization] requested "${requested}", using "${active}"`);
  return localization;
}
